import { TFunction } from 'i18next';
import { shortDate } from '../../../utils/formatters';

import { parseStartDate, parseEndDate } from '../../../utils/parseDate';

export const getPreviousYearDate = (date: Date): Date => {
    const newDate = new Date(date);
    newDate.setFullYear(newDate.getFullYear() - 1);
    return newDate;
};

export const getPreviousDaysDate = (date: Date, dateDiff: number): Date => {
    const newDate = new Date(date);
    newDate.setDate(newDate.getDate() - dateDiff);
    return newDate;
};

export const getDateDiff = ({ startDate, endDate }) => {
    return Math.round(
        (new Date(endDate).getTime() - new Date(startDate).getTime()) /
            (3600 * 1000 * 24),
    );
};

export const getPreviousDate = ({
    startDate,
    endDate,
}): {
    previous_year: {
        startDate: Date;
        endDate: Date;
    };
    previous_days: {
        startDate: Date;
        endDate: Date;
    };
} => {
    const dateDiff = getDateDiff({ startDate, endDate });

    return {
        previous_year: {
            startDate: startDate
                ? parseStartDate(getPreviousYearDate(startDate))
                : null,
            endDate: endDate
                ? parseEndDate(getPreviousYearDate(endDate))
                : parseEndDate(getPreviousYearDate(new Date())),
        },
        previous_days: {
            startDate: !Number.isNaN(dateDiff)
                ? parseStartDate(getPreviousDaysDate(startDate, dateDiff))
                : null,
            endDate: !Number.isNaN(dateDiff)
                ? parseEndDate(getPreviousDaysDate(endDate, dateDiff))
                : parseEndDate(new Date()),
        },
    };
};

export const getCompareOptions = (
    {
        startDate,
        endDate,
    }: {
        startDate: Date;
        endDate: Date;
    },
    key = 'name',
    addNone = false,
    t?: TFunction,
) => {
    const dateDiff = getDateDiff({ startDate, endDate });
    const previousDates = getPreviousDate({ startDate, endDate });
    const multipleDays: boolean = dateDiff > 1;
    const formatDate = date => {
        if (typeof date === 'string' && !Number.isNaN(Date.parse(date))) {
            return shortDate(date);
        }
        return undefined;
    };

    const formattedPrevYearStart = formatDate(
        previousDates.previous_year.startDate,
    );
    const formattedPrevYearEnd = formatDate(
        previousDates.previous_year.endDate,
    );
    const formattedPrevDaysStart = formatDate(
        previousDates.previous_days.startDate,
    );
    const formattedPrevDaysEnd = formatDate(
        previousDates.previous_days.endDate,
    );

    // Generate labels with translation if t is provided, otherwise use fallback
    const noneLabel = t ? t('dataExplorer.compareOptions.none') : 'None';

    const sameLastYearLabel = t
        ? t('dataExplorer.compareOptions.sameLastYear', {
              count: dateDiff,
              startDate: formattedPrevYearStart,
              endDate: formattedPrevYearEnd,
          })
        : `Same ${dateDiff} day${multipleDays ? 's' : ''} last year (${formattedPrevYearStart} - ${formattedPrevYearEnd})`;

    const previousDaysLabel = t
        ? t('dataExplorer.compareOptions.previousDays', {
              count: dateDiff,
              startDate: formattedPrevDaysStart,
              endDate: formattedPrevDaysEnd,
          })
        : `Previous ${dateDiff} day${multipleDays ? 's' : ''} (${formattedPrevDaysStart} - ${formattedPrevDaysEnd})`;

    return [
        ...(addNone
            ? [
                  {
                      value: null,
                      [key]: noneLabel,
                  },
              ]
            : []),
        {
            value: 'previous_year',
            [key]: sameLastYearLabel,
        },
        {
            value: 'previous_days',
            [key]: previousDaysLabel,
        },
    ];
};
